// LoadingScreen — mechanical-themed white-background loader.
//
// Replaces the plain "pulsing icon" fallback the app used to show while
// data was being fetched. The loader is themed for a manufacturing ERP:
//   • Solid white background so it feels crisp and premium.
//   • Two large interlocked SVG gears rotate continuously; the smaller
//     driver spins clockwise, the larger driven wheel spins counter-
//     clockwise at 66% speed (real gear ratio).
//   • A row of small tooth-dots rotates around the pair for atmosphere.
//   • Below the gears, a rotating tip carousel keeps the operator
//     engaged — cross-fades every 3.4 s from a curated list of factory-
//     quality tips.
//   • No linear progress bar — the gears themselves ARE the progress
//     indicator, matching the user's ask.
//
// API:
//   <LoadingScreen                    // full-screen (default)
//     label="Signing you in…"         // optional main line
//     tips={[…]}                      // optional custom tip list
//   />

import { useEffect, useState } from "react";

const DEFAULT_TIPS = [
  "Tip • Update RM stock in the morning to reflect the day's true starting inventory.",
  "Tip • Lock past months in the period selector to prevent accidental edits.",
  "Tip • Use BOM Routing to auto-project rates → no re-typing per part.",
  "Tip • Firewall inspection catches defects earlier → less scrap downstream.",
  "Tip • Set opening balances once, then let the system flow WIP daily.",
  "Tip • Categorise operations (M / S/F / RFD) so the DRS rollup lines up.",
  "Quality first — a rejected part costs 5× a caught one.",
  "Tip • Bookmark Dashboard, Sales Register and Reports — the pages you'll use daily.",
];

export default function LoadingScreen({ label = "Loading your factory…", tips = DEFAULT_TIPS }) {
  const [tipIdx, setTipIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setTipIdx((i) => (i + 1) % tips.length), 3400);
    return () => clearInterval(t);
  }, [tips.length]);

  return (
    <>
      <style>{`
        @keyframes lsGearCW  { from { transform: rotate(0deg); }  to { transform: rotate(360deg); } }
        @keyframes lsGearCCW { from { transform: rotate(0deg); }  to { transform: rotate(-360deg); } }
        @keyframes lsGearMd  { from { transform: rotate(0deg); }  to { transform: rotate(240deg); } }
        @keyframes lsTipFade {
          0%   { opacity: 0; transform: translateY(4px); }
          15%  { opacity: 1; transform: translateY(0); }
          85%  { opacity: 1; transform: translateY(0); }
          100% { opacity: 0; transform: translateY(-4px); }
        }
        @keyframes lsDotDrift {
          0%,100% { transform: scale(1); }
          50%     { transform: scale(1.6); }
        }
      `}</style>

      <div className="min-h-screen bg-white flex items-center justify-center relative overflow-hidden" data-testid="loading-screen">
        {/* Soft golden radial glow behind gears */}
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(circle at center, rgba(240,184,75,0.14) 0%, transparent 55%)" }} />

        {/* Faint background gears */}
        <BgGear className="absolute -top-16 -left-16 opacity-[0.05] text-[#D4A843]" size={280} anim="lsGearCW 30s linear infinite" />
        <BgGear className="absolute -bottom-24 -right-16 opacity-[0.05] text-[#F0B84B]" size={340} anim="lsGearCCW 40s linear infinite" />

        <div className="relative z-10 flex flex-col items-center gap-8 max-w-md px-6">
          {/* ── Interlocked gear pair ── */}
          <div className="relative" style={{ width: 220, height: 180 }}>
            {/* Big driven gear (bottom-left) */}
            <svg className="absolute" viewBox="0 0 100 100" width="150" height="150"
              style={{
                top: 20, left: -6,
                animation: "lsGearCCW 4.2s linear infinite",
                transformOrigin: "50% 50%",
                color: "#D4A843",
              }}>
              <Gear12 />
            </svg>
            {/* Small driver gear (top-right) */}
            <svg className="absolute" viewBox="0 0 100 100" width="102" height="102"
              style={{
                top: -4, right: -2,
                animation: "lsGearCW 2.8s linear infinite",
                transformOrigin: "50% 50%",
                color: "#B45309",
              }}>
              <Gear8 />
            </svg>
            {/* Tiny idler gear at bottom-right for extra motion */}
            <svg className="absolute" viewBox="0 0 100 100" width="62" height="62"
              style={{
                bottom: 6, right: 10,
                animation: "lsGearMd 1.9s linear infinite",
                transformOrigin: "50% 50%",
                color: "#1D2951",
              }}>
              <Gear8 />
            </svg>

            {/* Orbit dots */}
            {[0, 60, 120, 180, 240, 300].map((deg, i) => (
              <span key={i}
                className="absolute w-2 h-2 rounded-full bg-[#F0B84B]"
                style={{
                  left: "50%", top: "50%",
                  transform: `rotate(${deg}deg) translate(110px) rotate(-${deg}deg)`,
                  animation: `lsDotDrift 1.8s ease-in-out ${i * 0.22}s infinite`,
                }} />
            ))}
          </div>

          {/* Label */}
          <div className="text-center">
            <div className="text-[11px] uppercase tracking-[0.4em] font-black text-[#D4A843] mb-1">Swami Software</div>
            <div className="text-lg md:text-xl font-black text-[#1D2951]" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {label}
            </div>
          </div>

          {/* Rotating tip */}
          <div className="min-h-[54px] flex items-center justify-center max-w-[420px]">
            <p key={tipIdx}
              className="text-center text-[13px] leading-relaxed text-[#475569] px-3 py-2 border border-dashed border-[#F0B84B]/60 rounded-lg bg-[#FFFBEB]/60"
              style={{ animation: "lsTipFade 3.4s ease-in-out" }}>
              {tips[tipIdx]}
            </p>
          </div>
        </div>
      </div>
    </>
  );
}

function BgGear({ className = "", size = 200, anim }) {
  return (
    <svg className={className} width={size} height={size} viewBox="0 0 100 100" fill="currentColor"
      style={{ animation: anim, transformOrigin: "50% 50%" }}>
      <Gear12 />
    </svg>
  );
}

/* 12-tooth gear (used for large / background gears). */
function Gear12() {
  return (
    <path d="M50 8 L54 -2 L58 8 L68 6 L67 16 L76 22 L70 30 L79 36 L72 42 L79 49 L70 56 L76 62 L67 70 L68 78 L58 76 L54 86 L50 76 L46 86 L42 76 L32 78 L33 70 L24 62 L30 56 L21 49 L28 42 L21 36 L30 30 L24 22 L33 16 L32 6 L42 8 L46 -2 Z M50 32 A18 18 0 1 0 50 68 A18 18 0 1 0 50 32 Z" />
  );
}
/* 8-tooth gear (used for small / driver gear). */
function Gear8() {
  return (
    <path d="M50 6 L60 2 L62 14 L74 8 L74 22 L86 24 L80 34 L92 40 L82 48 L92 60 L80 66 L86 76 L74 78 L74 92 L62 86 L60 98 L50 94 L40 98 L38 86 L26 92 L26 78 L14 76 L20 66 L8 60 L18 48 L8 40 L20 34 L14 24 L26 22 L26 8 L38 14 L40 2 Z M50 30 A20 20 0 1 0 50 70 A20 20 0 1 0 50 30 Z" fill="currentColor" />
  );
}
